import { SUPPORT_USER_ID } from './support-user';

/**
 * Task assignment — `tasks/task-assignment.md`.
 *
 * NO ASSIGNEES MEANS THE WHOLE PRACTICE. Every task created before assignment existed has none, and so does
 * any task saved without choosing anybody.
 */

/** One person on a task, as the server sends it (`utils/patientTaskAssignees.js` → `serializeAssignees`). */
export interface TaskAssignee {
  userId: string;
  name: string;
}

/** One person the "Assigned to" select offers. */
export interface AssignableMember {
  userId: string;
  name: string;
}

const sameId = (a: unknown, b: unknown): boolean =>
  typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

/**
 * Who a task can be assigned to, from `GroupService.groupUsers$`.
 *
 * NOT the treating-physician filter (`userType === 'doctor'`). Receptionists and nurses are
 * `userType: 'support'`, and they are the people most tasks go to. Excluded:
 *   · the MedicAI support user — as in every other people picker;
 *   · pending invites, which the same list carries with `userId: null` — nobody to assign yet.
 */
export function taskAssignableMembers(groupUsers: any[] | null | undefined): AssignableMember[] {
  return (groupUsers || [])
    .filter((u: any) => !!u && typeof u.userId === 'string' && u.userId && !sameId(u.userId, SUPPORT_USER_ID))
    .map((u: any) => ({
      userId: u.userId as string,
      name: [u.title, u.name, u.surname].map((p: any) => (p == null ? '' : String(p).trim())).filter(Boolean).join(' ') || 'Unnamed member',
      sortKey: `${String(u.surname || '').toLowerCase()} ${String(u.name || '').toLowerCase()}`,
    }))
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ userId, name }) => ({ userId, name }));
}

/** The assignee ids on a task, for a form control. Never undefined. */
export function taskAssigneeIds(task: any): string[] {
  return Array.isArray(task?.assignees) ? task.assignees.map((a: TaskAssignee) => a.userId).filter(Boolean) : [];
}

/**
 * Whether a task is this user's to do: nobody is assigned (the whole practice), or they are one of the
 * assignees. The "Pending Tasks for Today" popup, the "Task List (N)" count and the list's Mine filter all
 * use this, so they agree.
 */
export function isTaskForUser(task: any, userId: string | null | undefined): boolean {
  const assignees: TaskAssignee[] = Array.isArray(task?.assignees) ? task.assignees : [];
  if (!assignees.length) return true;
  return !!userId && assignees.some((a) => sameId(a.userId, userId));
}

/** "Everyone", or the names. What a list row prints after "Assigned to". */
export function taskAssigneeSummary(task: any): string {
  const assignees: TaskAssignee[] = Array.isArray(task?.assignees) ? task.assignees : [];
  return assignees.length ? assignees.map((a) => a.name).join(', ') : 'Everyone';
}
